import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import InputBase from "../../../../common/components/form/InputBase.jsx";
import { YoutubeIdFromLink } from "../../../lib/youtube.js";
import bytes2hr from "../../../lib/bytes2hr.js";

const ECommerceProductPicturesMaxNumber = 10;
const ECommerceProductPictureMaxFileSize = 10 * 1024 * 1024;

const VK = "PhotosNVideos"; // VariablesKey
export default function PhotosNVideos(props) {
    const { t } = useTranslation();
    const { set, variables } = props;

    const refProductPictures = React.useRef(null);
    async function onProductPictureSelect(event) {
        const domInput = event.target;

        // Test max pictures number
        if (domInput.files.length > ECommerceProductPicturesMaxNumber) {
            toast.error(`Maximum number of pictures - ${ECommerceProductPicturesMaxNumber}`);
        }

        // Test max file size
        const files = [];

        for (const file of domInput.files) {
            if (files.length >= ECommerceProductPicturesMaxNumber) {
                break;
            }

            // Test max pictures number
            if (file.size > ECommerceProductPictureMaxFileSize) {
                domInput.value = "";
                toast.error(`Maximum filesize of pictures - ${bytes2hr(ECommerceProductPictureMaxFileSize)}`);
                return;
            }

            files.push({ file });
        }

        // Read picutres
        // > Example InputFile.jsx
        const data = await Promise.all(
            files.map(
                ({ file }) =>
                    new Promise(resolve => {
                        const FR = new FileReader();
                        FR.addEventListener("load", event =>
                            resolve({
                                file: {
                                    name: file.name,
                                    type: file.type,
                                    dataUrlBase64: event.target.result,
                                },
                            })
                        );
                        FR.readAsDataURL(file);
                    })
            )
        );

        set({
            ...variables,
            [VK]: [...(variables[VK] || []), ...data] //
                .reverse()
                .slice(0, ECommerceProductPicturesMaxNumber)
                .reverse(),
        });
    }

    function onAddProductPictureClick() {
        refProductPictures.current.click();
    }

    function onRemoveProductPictureClick(file) {
        set({
            ...variables,
            [VK]: (variables[VK] || []).filter(_file => _file !== file),
        });
    }

    function onYoutubeChange(event) {
        const link = event.target.value.trim();
        const id = YoutubeIdFromLink(link);

        if (variables[VK]?.[0]?.type !== "youtube") {
            if (link) {
                variables[VK] = [{ type: "youtube", id, link }, ...(variables[VK] || [])];
            }
        } else {
            if (link) {
                variables[VK][0] = { ...variables[VK][0], id, link };
            } else {
                variables[VK] = variables[VK].slice(1);
            }
        }

        set({ ...variables, [VK]: [...(variables[VK] || [])] });
    }

    // Max columns of product pictures
    let productPicturesGridColumns = 0;
    if ((variables[VK] || []).length === 0) {
        productPicturesGridColumns = 0;
    } else if ((variables[VK] || []).length < ECommerceProductPicturesMaxNumber) {
        productPicturesGridColumns += 1;
    }

    productPicturesGridColumns = Math.min(5, productPicturesGridColumns);

    const _youtube = (variables[VK] || []).filter(({ type }) => type === "youtube")[0];
    const _photos = (variables[VK] || []).filter(({ type }) => type !== "youtube");

    const _youtubeInputProps = {};
    if (_youtube?.error) {
        _youtubeInputProps.error = _youtube.error;
    }

    return (
        <>
            <input
                ref={refProductPictures}
                type="file"
                maxLength={ECommerceProductPicturesMaxNumber}
                accept="image/jpeg,image/png,image/webp,image/avif"
                multiple
                style={{ display: "none" }}
                onChange={onProductPictureSelect}
            />

            <>
                <h3 className="text-left">
                    {t("PhotosAndVideos")}{" "}
                    {/* {t("CountOfMax")
                        .replace(/%COUNT%/g, _photos.length)
                        .replace(/%MAX%/g, ECommerceProductPicturesMaxNumber)} */}
                </h3>

                <p className="text-left">
                    {t("ECommercePhotosNVideosHint")
                        .replace(/%count%/g, ECommerceProductPicturesMaxNumber)
                        .replace(/%size%/g, bytes2hr(ECommerceProductPictureMaxFileSize))}
                </p>

                <InputBase
                    onChange={onYoutubeChange}
                    onBlur={onYoutubeChange}
                    value={_youtube?.link || ""}
                    placeholder={t("Put YouTube Video Link Here")}
                    fullWidth
                    {..._youtubeInputProps}
                />

                <div
                    className="wizard-ecommerce-product-picture grid-gap-20 justify-items-start"
                    style={{ gridTemplateColumns: `repeat(${Math.min(5, _photos.length + 1)}, 1fr)` }}
                >
                    {(_photos || []).map((file, index) => (
                        <React.Fragment key={index}>
                            <ECommerceProductPicture
                                src={file?.src}
                                dataUrlBase64={file?.file?.dataUrlBase64}
                                onClick={() => onRemoveProductPictureClick(file)}
                            />
                        </React.Fragment>
                    ))}

                    {(_photos || []).length < ECommerceProductPicturesMaxNumber && (
                        <ECommerceProductPicture onClick={onAddProductPictureClick}>+</ECommerceProductPicture>
                    )}
                </div>
            </>
        </>
    );
}

function ECommerceProductPicture(props) {
    const { src, dataUrlBase64, children } = props;

    const wImage = src || dataUrlBase64;

    const cn = ["wizard-ecommerce-product-picture-button"];
    wImage && cn.push("with-image");

    const background = !wImage ? `transparent` : `transparent url("${src || dataUrlBase64}") center / cover no-repeat`;

    return (
        <button className={cn.join(" ")} onClick={props?.onClick} style={{ background }}>
            {wImage && <div className="remove" />}
            {children}
        </button>
    );
}
